/**
 * Audit log: `audit.view` (server-enforced), or `audit.view.signin` for the user entries only; read-only. Every action taken on
 * the platform, including refused and failed ones, newest first. Modelled on
 * GftUs.Atlas's Audit log viewer (f1.5), in the house list layout (one white
 * card: filter toolbar, plain-header table, pagination):
 *
 * - keyset paging behind the shared Pagination footer: Previous / Next walk
 *   the server's cursors, so the footer shows "Page 2" and the rows on it but
 *   never a page count or total, since both need a COUNT over a table that
 *   only grows. The When column flips newest / oldest first;
 * - filters in the columns' order: one date filter (quick ranges, a day or
 *   a range), multi-select actor, action, entity and outcome, and a source
 *   IP (one address, a CIDR block or a range);
 * - times read in local time or UTC, a per-browser choice;
 * - rows expand to the full entry: request, before and after side by side;
 * - over the card, the summary strip counts what the filters match;
 * - read tracing: while it is on, a banner over the filters says who started it, for whom, why and when it ends
 *   (every audit reader sees it); holders of `audit.tracing.manage` start it with Trace reads and stop it from the
 *   banner. The reads it records (the READ family) show only with Show traced reads, marked Traced.
 */

import { Fragment } from 'react';
import { ChevronDown, ScrollText } from 'lucide-react';

import type { AuditEntrySummary } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { Pagination } from '@/ui/Pagination';
import { RefreshButton } from '@/ui/RefreshButton';
import { SortHeader } from '@/ui/SortHeader';
import { Badge } from '@/ui/Badge';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure } from '@/ui/StateCard';

import { AuditEntryDetail } from './AuditEntryDetail';
import { AuditFilterBar } from './AuditFilterBar';
import { AuditSummary } from './AuditSummary';
import { formatAuditTime } from './auditTime';
import { outcomePill } from './outcomePill';
import { TraceReadsButton } from './TraceReadsDialog';
import { TracingBanner } from './TracingBanner';
import { type AuditLogState, useAuditLogState } from './useAuditLogState';

const COLUMNS = 6;

const FULL_SUBTITLE = 'Every action on the platform, including the ones refused or failed';
// The narrow key reads only sign-ins and account changes; the server limits the entries and the entity filter.
const SIGNIN_SUBTITLE = 'Sign-ins and changes to accounts, including the ones refused or failed';

export function AuditLogPage() {
  const state = useAuditLogState();
  const { can } = useSession();
  const { log, summary } = state;

  return (
    <>
      <PageHead title="Audit Log" subtitle={can('audit.view') ? FULL_SUBTITLE : SIGNIN_SUBTITLE}>
        {can('audit.tracing.manage') && <TraceReadsButton />}
        <RefreshButton queryKeys={[['audit']]} />
      </PageHead>

      {summary.data && !log.error && (
        <AuditSummary data={summary.data} utc={state.utc} outcomes={state.values.outcomes} onOutcome={state.set.outcomes} />
      )}

      <TracingBanner />

      {log.error ? (
        <LoadFailure error={log.error} onRetry={() => void log.refetch()} />
      ) : (
        <div className="card list-card">
          <AuditFilterBar state={state} />
          <AuditTable state={state} />
          <AuditPagination state={state} />
        </div>
      )}
    </>
  );
}

function AuditTable({ state }: { state: AuditLogState }) {
  return (
    <div className="table-scroll">
      <table className="data compact audit-table">
        <thead>
          <tr>
            <th scope="col" className="audit-toggle-cell"><span className="sr-only">Details</span></th>
            <SortHeader
              label={state.utc ? 'When (UTC)' : 'When'}
              sortKey="occurred_at"
              sort="occurred_at"
              order={state.order}
              onSort={state.flipOrder}
            />
            <th scope="col">Actor</th>
            <th scope="col">Action</th>
            <th scope="col">Entity</th>
            <th scope="col">Outcome</th>
            <th scope="col">Source IP</th>
          </tr>
        </thead>
        <tbody>
          <AuditRows state={state} />
        </tbody>
      </table>
    </div>
  );
}

function AuditRows({ state }: { state: AuditLogState }) {
  const { log, entries } = state;
  if (log.isPending) {
    return (
      <>
        {Array.from({ length: 6 }, (_, i) => (
          <tr key={i} className="skeleton-row" aria-hidden="true">
            <td />
            {Array.from({ length: COLUMNS }, (__, c) => (
              <td key={c}><span className="skeleton-bar" /></td>
            ))}
          </tr>
        ))}
      </>
    );
  }
  if (entries.length === 0) {
    return (
      <tr>
        <td colSpan={COLUMNS + 1}>
          <div className="empty">
            <div className="e-icon"><ScrollText aria-hidden="true" /></div>
            <h3>No entries match</h3>
            <p>{state.filtered ? 'Widen the date range or clear the filters.' : 'Nothing has been recorded in this period.'}</p>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <>
      {entries.map((entry, i) => (
        <AuditRow
          key={entry.id}
          entry={entry}
          utc={state.utc}
          striped={i % 2 === 1}
          expanded={state.expanded === entry.id}
          onToggle={() => state.toggle(entry.id)}
        />
      ))}
    </>
  );
}

function AuditPagination({ state }: { state: AuditLogState }) {
  const { log, entries } = state;
  if (entries.length === 0) return null;
  return (
    <Pagination
      page={state.page}
      pageSize={state.pageSize}
      shown={entries.length}
      hasNext={Boolean(log.data?.has_more) && !log.isPlaceholderData}
      noun="entries"
      onPage={state.goTo}
      onPageSize={state.set.pageSize}
    />
  );
}

/** The actor, and under it the AI tool or personal token it acted through. */
function ActorCell({ entry }: { entry: AuditEntrySummary }) {
  return (
    <td>
      <span className={`audit-actor${entry.actor_user_id ? '' : ' is-unverified'}`} title={entry.actor_display}>
        {entry.actor_display}
      </span>
      {entry.via && <span className="sub">via {entry.via}</span>}
    </td>
  );
}

function AuditRow({
  entry,
  utc,
  striped,
  expanded,
  onToggle,
}: {
  entry: AuditEntrySummary;
  utc: boolean;
  /** Every other entry is tinted; its detail row takes the same tint. */
  striped: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const detailId = `audit-detail-${entry.id}`;
  const outcome = outcomePill(entry.outcome);
  return (
    <Fragment>
      <tr className={`audit-row outcome-${entry.outcome.toLowerCase()}${striped ? ' is-striped' : ''}${expanded ? ' is-expanded' : ''}`}>
        <td className="audit-toggle-cell">
          <button
            type="button"
            className="icon-btn audit-toggle"
            aria-expanded={expanded}
            aria-controls={detailId}
            aria-label={expanded ? 'Hide details' : 'Show details'}
            onClick={onToggle}
          >
            <ChevronDown aria-hidden="true" />
          </button>
        </td>
        <td className="mono audit-ts" title={entry.occurred_at}>{formatAuditTime(entry.occurred_at, utc)}</td>
        <ActorCell entry={entry} />
        <td><span className="badge badge-chip mono">{entry.action}</span></td>
        <td className={entry.entity_type ? undefined : 'hint'}>{entry.entity_type ?? 'none'}</td>
        <td>
          <span className={`badge ${outcome.className}`}>{outcome.label}</span>
          {entry.event_family === 'READ' && <> <Badge tone="info" title="Recorded while read tracing was on">Traced</Badge></>}
        </td>
        <td className={entry.source_ip ? 'mono' : 'mono hint'}>{entry.source_ip ?? 'unknown'}</td>
      </tr>
      {expanded && (
        <tr id={detailId} className="audit-detail-row">
          <td colSpan={COLUMNS + 1}>
            <AuditEntryDetail entryId={entry.id} utc={utc} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}
